/**
 * Shared enums, limits and defaults. Values here are the single source of truth
 * for both the API and the frontends (BLUEPRINT.md sections 5-6, 10).
 */

export const LANGUAGES = ['en', 'am'] as const;
export type Language = (typeof LANGUAGES)[number];

/** PWA account passwords. Staff passwords stay at 12 characters. */
export const USER_PASSWORD_MIN_LENGTH = 8;

export const THEMES = ['light', 'dark', 'system'] as const;
export type Theme = (typeof THEMES)[number];

export const CATEGORIES = ['sports', 'economy', 'tech_telecom', 'politics', 'culture'] as const;
export type Category = (typeof CATEGORIES)[number];

export const MARKET_STATUSES = ['draft', 'open', 'locked', 'resolved', 'voided'] as const;
export type MarketStatus = (typeof MARKET_STATUSES)[number];

export const SETTLEMENT_STATUSES = ['none', 'in_progress', 'done'] as const;
export type SettlementStatus = (typeof SETTLEMENT_STATUSES)[number];

export const POSITION_STATUSES = ['active', 'won', 'lost', 'refunded'] as const;
export type PositionStatus = (typeof POSITION_STATUSES)[number];

export const PAYMENT_METHODS = ['telebirr', 'cbe', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const TOPUP_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type TopUpStatus = (typeof TOPUP_STATUSES)[number];

export const WITHDRAWAL_STATUSES = [
  'pending',
  'processing',
  'completed',
  'rejected',
  'cancelled',
] as const;
export type WithdrawalStatus = (typeof WITHDRAWAL_STATUSES)[number];

export const LEDGER_TYPES = [
  'topup_credit',
  'stake',
  'payout',
  'refund',
  'withdrawal_hold',
  'withdrawal_release',
  'withdrawal_complete',
  'adjustment',
] as const;
export type LedgerType = (typeof LEDGER_TYPES)[number];

export const ADMIN_ROLES = ['super_admin', 'finance', 'moderator'] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

export const USER_STATUSES = ['active', 'suspended'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const PROPOSAL_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];

/** A user may have at most this many proposals still waiting for review. */
export const MAX_PENDING_PROPOSALS = 5;

export const NOTIFICATION_TYPES = [
  'topup_approved',
  'topup_rejected',
  'position_won',
  'position_lost',
  'position_refunded',
  'withdrawal_processing',
  'withdrawal_completed',
  'withdrawal_rejected',
  'market_voided',
  'proposal_approved',
  'proposal_rejected',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export const MARKET_SORTS = ['trending', 'closing', 'new'] as const;
export type MarketSort = (typeof MARKET_SORTS)[number];

/** Money. 1 ETB = 100 santim. All money in the system is an integer number of santim. */
export const SANTIM_PER_ETB = 100;

/** Outcome count limits for a market. */
export const MIN_OUTCOMES = 2;
export const MAX_OUTCOMES = 6;

/** Pagination. */
export const DEFAULT_PAGE_LIMIT = 20;
export const MAX_PAGE_LIMIT = 50;

/** Platform fee can never be configured above this (basis points). */
export const MAX_FEE_BPS = 2000;

/** Defaults for the singleton Config document (BLUEPRINT.md section 5.2). Amounts in santim. */
export const DEFAULT_CONFIG = {
  feeBps: 700,
  minStake: 1_000,
  maxStake: 1_000_000,
  maxUserPerMarket: 5_000_000,
  minTopUp: 5_000,
  minWithdrawal: 10_000,
  maxPendingWithdrawals: 3,
  requireTwoPersonResolution: true,
  largeTopUpThreshold: 10_000_000,
  largeWithdrawalThreshold: 5_000_000,
  minTurnoverRatio: 0,
} as const;

export interface DepositAccount {
  method: PaymentMethod;
  accountName: string;
  accountNumber: string;
  instructions: { en: string; am: string };
}

/** Quick-select stake chips shown in the prediction drawer (santim). */
export const QUICK_STAKES_SANTIM = [5_000, 10_000, 50_000] as const;

/** Error codes and the HTTP status each maps to (BLUEPRINT.md section 10). */
export const ERROR_STATUS = {
  VALIDATION_ERROR: 400,
  UNAUTHENTICATED: 401,
  INVALID_OTP: 401,
  EMAIL_NOT_VERIFIED: 403,
  FORBIDDEN: 403,
  AGE_NOT_CONFIRMED: 403,
  ACCOUNT_SUSPENDED: 403,
  SAME_ADMIN: 403,
  NOT_FOUND: 404,
  INVALID_STATE: 409,
  DUPLICATE_REFERENCE: 409,
  EMAIL_IN_USE: 409,
  MARKET_CLOSED: 409,
  KYC_REQUIRED: 412,
  LIMIT_EXCEEDED: 422,
  INSUFFICIENT_FUNDS: 422,
  INVALID_PHONE: 422,
  RATE_LIMITED: 429,
  INTERNAL: 500,
} as const;
export type ErrorCode = keyof typeof ERROR_STATUS;
export const ERROR_CODES = Object.keys(ERROR_STATUS) as ErrorCode[];
